import { state, prefs, debouncedSave, notify } from './state.js';

// ================================================================
// UNICODE PANEL
// ================================================================
export const UNICODE_CATEGORIES = [
  { name: "Block Elements", start: 0x2580, end: 0x259F },
  { name: "Box Drawing", start: 0x2500, end: 0x257F },
  { name: "Geometric Shapes", start: 0x25A0, end: 0x25FF },
  { name: "Braille Patterns", start: 0x2800, end: 0x28FF },
  { name: "Arrows", start: 0x2190, end: 0x21FF },
  { name: "Math Operators", start: 0x2200, end: 0x22FF },
  { name: "Misc Technical", start: 0x2300, end: 0x23FF },
  { name: "Misc Symbols", start: 0x2600, end: 0x26FF },
  { name: "Dingbats", start: 0x2700, end: 0x27BF },
  { name: "CJK Symbols", start: 0x3000, end: 0x303F },
  { name: "Enclosed Alphanumerics", start: 0x2460, end: 0x24FF },
  { name: "Letterlike Symbols", start: 0x2100, end: 0x214F },
  { name: "Number Forms", start: 0x2150, end: 0x218F },
  { name: "Currency Symbols", start: 0x20A0, end: 0x20CF },
  { name: "Playing Cards", start: 0x1F0A0, end: 0x1F0FF },
  { name: "Domino Tiles", start: 0x1F030, end: 0x1F09F },
  { name: "Mahjong Tiles", start: 0x1F000, end: 0x1F02F },
  { name: "Musical Symbols", start: 0x1D100, end: 0x1D1FF },
  { name: "Emoticons", start: 0x1F600, end: 0x1F64F },
  { name: "Misc Symbols & Pictographs", start: 0x1F300, end: 0x1F5FF },
  { name: "Supplemental Arrows-B", start: 0x2900, end: 0x297F },
  { name: "Symbols & Pictographs Ext-A", start: 0x1FA70, end: 0x1FAFF },
  { name: "Latin Extended-A", start: 0x0100, end: 0x017F },
  { name: "Greek and Coptic", start: 0x0370, end: 0x03FF },
  { name: "Cyrillic", start: 0x0400, end: 0x04FF },
  { name: "General Punctuation", start: 0x2000, end: 0x206F },
  { name: "Superscripts & Subscripts", start: 0x2070, end: 0x209F },
];

export function renderUnicodePanel() {
  const panel = document.getElementById("unicode-panel");
  panel.innerHTML = "";

  // Search
  const searchDiv = document.createElement("div");
  searchDiv.className = "unicode-search";
  const searchInput = document.createElement("input");
  searchInput.type = "text";
  searchInput.placeholder = "Search or paste char / U+hex...";
  searchInput.value = state.ui.unicodeSearch;
  searchInput.addEventListener("input", () => {
    state.ui.unicodeSearch = searchInput.value;
    renderUnicodeCategories(catContainer);
  });
  searchDiv.appendChild(searchInput);
  panel.appendChild(searchDiv);

  // Favorites
  if (prefs.favorites.length > 0) {
    const favSection = document.createElement("div");
    favSection.className = "unicode-favorites-section";
    const favLabel = document.createElement("div");
    favLabel.className = "unicode-fav-label";
    favLabel.textContent = "Favorites";
    favSection.appendChild(favLabel);
    const favChars = document.createElement("div");
    favChars.className = "unicode-fav-chars";
    for (const cp of prefs.favorites) {
      favChars.appendChild(createCharBtn(String.fromCodePoint(cp)));
    }
    favSection.appendChild(favChars);
    panel.appendChild(favSection);
  }

  // Recent
  if (prefs.recent.length > 0) {
    const recentDiv = document.createElement("div");
    recentDiv.className = "unicode-recent";
    const recentLabel = document.createElement("div");
    recentLabel.className = "unicode-recent-label";
    recentLabel.textContent = "Recent";
    recentDiv.appendChild(recentLabel);
    for (const cp of prefs.recent) {
      recentDiv.appendChild(createCharBtn(String.fromCodePoint(cp)));
    }
    panel.appendChild(recentDiv);
  }

  // Categories
  const catContainer = document.createElement("div");
  catContainer.className = "unicode-categories";
  renderUnicodeCategories(catContainer);
  panel.appendChild(catContainer);
}

function renderUnicodeCategories(container) {
  container.innerHTML = "";
  const search = state.ui.unicodeSearch.trim();

  // If search is a hex codepoint
  if (/^(U\+|0x)?[0-9a-fA-F]{1,6}$/i.test(search)) {
    const hex = search.replace(/^(U\+|0x)/i, "");
    const cp = parseInt(hex, 16);
    if (cp >= 0 && cp <= 0x10FFFF) {
      const header = document.createElement("div");
      header.className = "unicode-cat-header active";
      header.textContent = `U+${hex.toUpperCase()} Result`;
      container.appendChild(header);
      const chars = document.createElement("div");
      chars.className = "unicode-cat-chars open";
      chars.appendChild(createCharBtn(String.fromCodePoint(cp)));
      container.appendChild(chars);
      const start = Math.max(0, cp - 16);
      const end = Math.min(0x10FFFF, cp + 16);
      const nearbyHeader = document.createElement("div");
      nearbyHeader.className = "unicode-cat-header active";
      nearbyHeader.textContent = "Nearby";
      container.appendChild(nearbyHeader);
      const nearbyChars = document.createElement("div");
      nearbyChars.className = "unicode-cat-chars open";
      for (let i = start; i <= end; i++) {
        nearbyChars.appendChild(createCharBtn(String.fromCodePoint(i)));
      }
      container.appendChild(nearbyChars);
      return;
    }
  }

  // If search is a single pasted character
  if (search.length === 1 || (search.length === 2 && search.codePointAt(0) > 0xFFFF)) {
    const cp = search.codePointAt(0);
    const header = document.createElement("div");
    header.className = "unicode-cat-header active";
    header.textContent = `"${search}" U+${cp.toString(16).toUpperCase().padStart(4, "0")}`;
    container.appendChild(header);
    const chars = document.createElement("div");
    chars.className = "unicode-cat-chars open";
    chars.appendChild(createCharBtn(search));
    container.appendChild(chars);
    const start = Math.max(0, cp - 16);
    const end = Math.min(0x10FFFF, cp + 16);
    const nearbyHeader = document.createElement("div");
    nearbyHeader.className = "unicode-cat-header active";
    nearbyHeader.textContent = "Nearby";
    container.appendChild(nearbyHeader);
    const nearbyChars = document.createElement("div");
    nearbyChars.className = "unicode-cat-chars open";
    for (let i = start; i <= end; i++) {
      nearbyChars.appendChild(createCharBtn(String.fromCodePoint(i)));
    }
    container.appendChild(nearbyChars);
    return;
  }

  // Filter categories by name search
  const filteredCats = search
    ? UNICODE_CATEGORIES.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : UNICODE_CATEGORIES;

  for (const cat of filteredCats) {
    const header = document.createElement("div");
    header.className = "unicode-cat-header" + (state.ui.openCategory === cat.name ? " active" : "");
    const count = cat.end - cat.start + 1;
    header.innerHTML = `<span>${cat.name}</span><span style="font-size:10px;color:#446">${count}</span>`;
    header.addEventListener("click", () => {
      state.ui.openCategory = state.ui.openCategory === cat.name ? null : cat.name;
      renderUnicodeCategories(container);
    });
    container.appendChild(header);

    if (state.ui.openCategory === cat.name) {
      const chars = document.createElement("div");
      chars.className = "unicode-cat-chars open";
      for (let cp = cat.start; cp <= cat.end; cp++) {
        chars.appendChild(createCharBtn(String.fromCodePoint(cp)));
      }
      container.appendChild(chars);
    }
  }
}

function createCharBtn(ch) {
  const cp = ch.codePointAt(0);
  const btn = document.createElement("button");
  btn.className = "unicode-char-btn";
  if (state.ui.selectedChar === ch) btn.classList.add("selected-char");
  if (prefs.favorites.includes(cp)) btn.classList.add("is-fav");
  btn.textContent = ch;
  btn.title = `U+${cp.toString(16).toUpperCase().padStart(4, "0")}`;

  const star = document.createElement("span");
  star.className = "fav-star";
  star.textContent = "\u2605";
  btn.appendChild(star);

  btn.addEventListener("click", () => {
    state.ui.selectedChar = ch;
    notify("selectChar");
    document.querySelectorAll(".unicode-char-btn").forEach(b => {
      b.classList.toggle("selected-char", b.textContent.replace("\u2605", "") === ch);
    });
  });

  btn.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    toggleFavorite(cp);
    renderUnicodePanel();
  });

  return btn;
}

function toggleFavorite(cp) {
  const idx = prefs.favorites.indexOf(cp);
  if (idx >= 0) {
    prefs.favorites.splice(idx, 1);
  } else {
    prefs.favorites.push(cp);
  }
  debouncedSave();
}

export function addRecent(ch) {
  const cp = ch.codePointAt(0);
  prefs.recent = prefs.recent.filter(c => c !== cp);
  prefs.recent.unshift(cp);
  if (prefs.recent.length > 20) prefs.recent.pop();
  debouncedSave();
}
